import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Share2, Check } from 'lucide-react';

/**
 * Builds the public share URL using the *current* origin so that after
 * deployment the URL resolves to cccvob.com (not a preview/test domain).
 */
export const buildShareUrl = (kind, itemId) =>
  `${window.location.origin}/api/share/${kind}/${itemId}`;

/**
 * Trigger the native share sheet (with a copy-to-clipboard fallback) for any
 * sharable resource. Used both by the visible <ShareButton /> and by the
 * long-press handler attached to flyer / gallery images.
 */
export const triggerShare = async ({ kind, itemId, title, description }) => {
  const url = buildShareUrl(kind, itemId);
  const safeTitle = title || (kind === 'flyer' ? 'Vineyard Event' : 'Church Gallery');
  const text = description || safeTitle;

  if (navigator.share) {
    try {
      await navigator.share({ title: safeTitle, text, url });
      return 'shared';
    } catch {
      // user cancelled — fall through to clipboard
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    window.prompt('Copy this link to share:', url);
    return 'prompted';
  }
};

/**
 * Hook that returns event handlers attaching long-press → share behavior to
 * any element. Works on touch and mouse. ~550 ms hold triggers the share sheet.
 */
export const useLongPressShare = ({ kind, itemId, title, description, holdMs = 550 }) => {
  const timer = useRef(null);
  const fired = useRef(false);

  const start = (e) => {
    fired.current = false;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      fired.current = true;
      if (e?.preventDefault) try { e.preventDefault(); } catch (_) { /* ignore */ }
      await triggerShare({ kind, itemId, title, description });
    }, holdMs);
  };
  const cancel = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  // If long-press fired, suppress the click that follows on touch devices
  const onClickCapture = (e) => {
    if (fired.current) {
      e.stopPropagation();
      e.preventDefault();
      fired.current = false;
    }
  };
  return {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchMove: cancel,
    onTouchCancel: cancel,
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onContextMenu: (e) => {
      // On desktop right-click also offers our share UX
      e.preventDefault();
      triggerShare({ kind, itemId, title, description });
    },
    onClickCapture,
  };
};

/**
 * Wrapper that attaches long-press → share behavior to its children. Useful
 * inside `.map(...)` loops where calling the `useLongPressShare` hook directly
 * would violate React's rules of hooks.
 *
 *   <LongPressShare kind="flyer" itemId={f.id} title={f.title} description={f.description}>
 *     <SomeCard />
 *   </LongPressShare>
 */
export const LongPressShare = ({ kind, itemId, title, description, holdMs, children, className, style }) => {
  const handlers = useLongPressShare({ kind, itemId, title, description, holdMs });
  return (
    <div {...handlers} className={className} style={style}>
      {children}
    </div>
  );
};

/**
 * Visible round share-icon button. Two variants:
 *   - icon (default)  : 32-36px round badge, used inside grid overlays
 *   - pill            : chip-style with a "Share" label
 */
const ShareButton = ({
  kind,
  itemId,
  title = '',
  description = '',
  className = '',
  variant = 'icon',
  stopPropagation = true,
}) => {
  const [state, setState] = useState('idle');

  const handleClick = async (e) => {
    if (stopPropagation && e?.stopPropagation) e.stopPropagation();
    if (e?.preventDefault) e.preventDefault();
    const result = await triggerShare({ kind, itemId, title, description });
    if (result === 'copied') {
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    }
  };

  const showCheck = state === 'copied';

  if (variant === 'pill') {
    return (
      <motion.button
        type="button"
        onClick={handleClick}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-violet-700 text-xs font-bold shadow hover:shadow-lg transition-shadow ${className}`}
        data-testid={`share-${kind}-${itemId}`}
        title={showCheck ? 'Link copied!' : 'Share'}
      >
        {showCheck ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
        <span>{showCheck ? 'Copied' : 'Share'}</span>
      </motion.button>
    );
  }

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.92 }}
      className={`inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/95 backdrop-blur text-violet-700 shadow ring-2 ring-white/70 hover:bg-white hover:text-violet-900 transition-colors ${className}`}
      data-testid={`share-${kind}-${itemId}`}
      title={showCheck ? 'Link copied!' : 'Share this'}
      aria-label="Share"
    >
      {showCheck ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
    </motion.button>
  );
};

export default ShareButton;
